import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import FictiveLogo from "../../assets/fictive_logo_key_color.svg?url";
import * as ROUTES from "../../constants/routes";
import { useNavigateCompat } from "../../helpFunctions/hooks";
import { capitalizeFirstLetter } from "../../helpFunctions/session";
import { DataStoreContext } from "../DataStore/DataStoreContext";
import { AuthUserContext } from "../Session";
import SignOutButton from "../SignOut";
import CurrentChannelBadge from "./CurrentChannelBadge";
import { getDeploymentEnvironmentInfo, getFrontendTargetInfo } from "./channelInfo";
import NavList from "./components/NavList";
import UserAvatarLink from "./components/UserAvatarLink";
import ReportIssueButton from "./components/ReportIssueButton";
import {
  DesktopNavWrapper,
  HamburgerMenuButton,
  LogoImage,
  LogoImg,
  NavFooter,
  NavFooterSpacer,
  NavWrapper,
  SliderBlanket,
  StyledNavContainer,
} from "./style";
import { HamMenu } from "../../styles/icons";

const ROUTES_WITHOUT_NAV = [ROUTES.SIGN_UP, ROUTES.SIGN_IN];

const Navigation = () => {
  const { state } = useContext(DataStoreContext);
  const { authUser } = useContext(AuthUserContext);
  const navigate = useNavigateCompat();
  const location = useLocation();
  const deploymentEnvironmentInfo = useMemo(() => getDeploymentEnvironmentInfo(), []);
  const frontendTargetInfo = useMemo(() => getFrontendTargetInfo(), []);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const showNav = ROUTES_WITHOUT_NAV.find(route => location.pathname.startsWith(route)) === undefined;

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return !state.loading && showNav ? (
    <NavWrapper>
      <DesktopNavigation
        location={location.pathname}
        authUser={authUser}
        navigate={navigate}
        organization={state.organization}
        deploymentEnvironmentInfo={deploymentEnvironmentInfo}
        frontendTargetInfo={frontendTargetInfo}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
    </NavWrapper>
  ) : null;
};

const DesktopNavigation = ({
  location,
  authUser,
  navigate,
  organization,
  deploymentEnvironmentInfo,
  frontendTargetInfo,
  mobileMenuOpen,
  setMobileMenuOpen,
}) => {
  useEffect(() => {
    if (location) {
      document.title = `${organization?.settings?.appName || "Fictive Reality"} - ${capitalizeFirstLetter(location.replace("/", ""))}`;
    }
  }, [location, organization]);

  const logoUrl = organization?.settings?.appLogo || FictiveLogo;

  const handleClick = href => {
    navigate(href);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(open => !open);
  };

  const handleMobileMenuHide = () => {
    setMobileMenuOpen(false);
  };

  const navContent = (
    <>
      <LogoImage onClick={handleMobileMenuHide}>
        <LogoImg src={logoUrl} alt="App logo" />
      </LogoImage>
      <CurrentChannelBadge />
      <NavList handleClick={handleClick} authUser={authUser} currentPath={location} organization={organization} />
      <NavFooterSpacer />
      <NavFooter>
        <ReportIssueButton onClick={handleMobileMenuHide} />
        {authUser && (
          <>
            <UserAvatarLink authUser={authUser} />
            <SignOutButton navigate={navigate} />
          </>
        )}
      </NavFooter>
    </>
  );

  return (
    <>
      <DesktopNavWrapper />
      <HamburgerMenuButton $hidden={mobileMenuOpen} onClick={handleMobileMenuToggle}>
        <HamMenu />
      </HamburgerMenuButton>
      {mobileMenuOpen && <SliderBlanket onClick={handleMobileMenuHide} />}
      <StyledNavContainer
        $specialDeployment={deploymentEnvironmentInfo.hasSpecialNavTone || frontendTargetInfo.hasSpecialNavTone}
        $mobileOpen={mobileMenuOpen}
      >
        {navContent}
      </StyledNavContainer>
    </>
  );
};

export default Navigation;
